// ---------------------------------------------------------------------------
// [C32] Shift matching and daily cash settlement (section 12, مطابقة الصناديق والورديات; D08 §5.1;
// D-120 – D-122, D-298, D-299; engineer decisions D-301 – D-306).
//
// Money is a decimal string everywhere (ADR-02 §2.9). A cash drawer is IQD or USD (D-118), so every figure of a
// shift or a day comes as a pair `{ IQD, USD }`. Times are ISO instants; office dates are `YYYY-MM-DD` in the office
// time zone (D-121, D-122). Requests are validated by the zod schemas; responses are described by the TypeScript
// interfaces (the API builds them, the web container maps them to the screen's props).
// ---------------------------------------------------------------------------
import { z } from "zod";
import { pinSchema } from "./schemas.js";

const uuid = z.string().uuid();
const officeDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** A typed cash count: a non-negative decimal (IQD integral, USD up to cents; the engine checks the 250 rule, D-075). */
const countedAmount = z.string().trim().regex(/^\d{1,15}(\.\d{1,2})?$/, "AMOUNT_FORMAT");

export const cashCountPairSchema = z.object({ IQD: countedAmount, USD: countedAmount }).strict();

/** `GET /shifts` filters of the shift log (old `filteredShifts`, App.tsx:623-636; office dates, D-122). */
export const shiftsQuerySchema = z
  .object({
    branchId: uuid.optional(),
    employee: uuid.optional(),
    from: officeDate.optional(),
    to: officeDate.optional(),
  })
  .strict();
export type ShiftsQuery = z.infer<typeof shiftsQuerySchema>;

/** `POST /shifts/open` — D-120: the owner (own, or another user with `open_shift_for_other`) and the counted cash. */
export const shiftOpenSchema = z
  .object({
    ownerUserId: uuid,
    branchId: uuid.optional(),
    counted: cashCountPairSchema,
    note: z.string().trim().max(500).optional(),
    pin: pinSchema,
  })
  .strict();
export type ShiftOpenInput = z.infer<typeof shiftOpenSchema>;

/** `POST /shifts/:id/close` — counted IQD / USD typed (no pre-fill, D-120), PIN of the owner or of a supervisor. */
export const shiftCloseSchema = z
  .object({
    counted: cashCountPairSchema,
    note: z.string().trim().max(500).optional(),
    pin: pinSchema,
  })
  .strict();
export type ShiftCloseInput = z.infer<typeof shiftCloseSchema>;

/** `GET /days` — the daily-settlement tab (D-121). */
export const daysQuerySchema = z
  .object({
    branchId: uuid.optional(),
    from: officeDate.optional(),
    to: officeDate.optional(),
  })
  .strict();
export type DaysQuery = z.infer<typeof daysQuerySchema>;

/** `POST /days/open` — the office's today only (D-302); one per office date and branch. */
export const dayOpenSchema = z
  .object({
    branchId: uuid.optional(),
    counted: cashCountPairSchema,
    note: z.string().trim().max(500).optional(),
    pin: pinSchema,
  })
  .strict();
export type DayOpenInput = z.infer<typeof dayOpenSchema>;

export const dayCloseSchema = shiftCloseSchema;
export type DayCloseInput = ShiftCloseInput;

/** `POST /cash-counts/:id/approve` — `approve_cash_differences` + own PIN (D-120, D-299). */
export const cashCountApproveSchema = z
  .object({
    /** Day differences only: another custody of the same vault (D-299 (e)); absent = the vault or the office custody. */
    postToAccountId: uuid.optional(),
    idempotencyKey: uuid,
    pin: pinSchema,
  })
  .strict();
export type CashCountApproveInput = z.infer<typeof cashCountApproveSchema>;

export const cashCountRejectSchema = z
  .object({
    reason: z.string().trim().min(1).max(500),
    pin: pinSchema,
  })
  .strict();
export type CashCountRejectInput = z.infer<typeof cashCountRejectSchema>;

/** `POST /shifts/:id/print`, `POST /days/:id/print` — audited (D-147 pattern). */
export const shiftPrintSchema = z.object({ document: z.enum(["opening", "report"]), channel: z.enum(["print", "pdf", "whatsapp"]) }).strict();

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export interface CurrencyPair {
  readonly IQD: string;
  readonly USD: string;
}

/** The figures of one drawer (or of a set of drawers, the day) over a window — `drawerFigures()` (D-298). */
export interface DrawerFiguresView {
  readonly accountId: string;
  readonly accountName: string | null;
  readonly currency: "IQD" | "USD";
  readonly opening: string;
  readonly receipts: string;
  readonly payments: string;
  /** "المصروفات التشغيلية" = expenses only (D-122). */
  readonly expenses: string;
  readonly custodyIn: string;
  readonly custodyOut: string;
  /** Approved differences posted in the window, signed. */
  readonly differences: string;
  /** Reversals in the window of operations from before it, signed (D-298 (a)). */
  readonly corrections: string;
  /** The drawer's ledger balance at the closing point. */
  readonly expected: string;
  /** Stored differences still waiting for approval, signed (D-299 (b)). */
  readonly pending: string;
  /** What a count is compared with: expected + pending. */
  readonly countBasis: string;
  readonly operationsCount: number;
}

export type CashCountScope = "shift_opening" | "shift_closing" | "day_opening" | "day_closing";
export type CashCountStatus = "matched" | "pending" | "approved" | "rejected";

/** One stored count of one currency (D-299). */
export interface CashCountView {
  readonly id: string;
  readonly scope: CashCountScope;
  readonly shiftId: string | null;
  readonly dayId: string | null;
  readonly currency: "IQD" | "USD";
  /** The drawer the difference posts to (custody, vault or — day — the office custody by default). */
  readonly accountId: string;
  readonly accountName: string | null;
  /** The user whose count it is (shift owner / the user who counted the day): never the approver. */
  readonly responsibleUserId: string;
  readonly responsible: string;
  readonly countedBy: string;
  /** [C32] id of the user who typed the count (never the decider, D-304). */
  readonly countedByUserId?: string;
  readonly countedAt: string;
  /** The figure counted against (countBasis at the count), the counted cash and counted − expected. */
  readonly expected: string;
  readonly counted: string;
  readonly difference: string;
  readonly kind: "matched" | "shortage" | "surplus";
  readonly status: CashCountStatus;
  readonly decidedBy: string | null;
  readonly decidedAt: string | null;
  readonly reason: string | null;
  readonly operationId: string | null;
  /** D-299 (f): the approved difference was cancelled through the journal afterwards (counted again at the next count). */
  readonly operationCancelled?: boolean;
  /** D-301: a count re-opened because a difference it was compared against was rejected. */
  readonly reopenedFromId: string | null;
}

export interface ShiftBoxLine {
  readonly accountId: string;
  readonly name: string;
  readonly currency: string;
  readonly receipts: string;
  readonly payments: string;
}

/** An open shift with its live figures (old active-shift card, App.tsx:36395-36661). */
export interface ShiftLiveView {
  readonly id: string;
  readonly branchId: string;
  readonly ownerUserId: string;
  readonly owner: string;
  readonly openedBy: string;
  readonly openedAt: string;
  readonly note: string | null;
  readonly drawers: { readonly IQD: DrawerFiguresView; readonly USD: DrawerFiguresView };
  /** "الأرصدة الافتتاحية الأخرى": every other vault / box of the branch at the opening point. */
  readonly otherOpening: CurrencyPair;
  /** Profits per currency of the shift's operations (the one profit engine, D-098). */
  readonly profits: CurrencyPair;
  readonly operationsCount: number;
  readonly expensesCount: number;
  /** Boxes other than the drawer that the shift's operations moved (old "موجز تفاصيل الحركة والسيولة لكل صندوق"). */
  readonly boxes: readonly ShiftBoxLine[];
  readonly counts: readonly CashCountView[];
}

/** A row of the shift log "سجل مطابقة وتسوية الورديات" (D-122). */
export interface ShiftRowView {
  readonly id: string;
  readonly branchId: string;
  readonly ownerUserId: string;
  readonly owner: string;
  readonly openedAt: string;
  readonly closedAt: string | null;
  readonly open: boolean;
  /** Book balance of the drawers at opening. */
  readonly opening: CurrencyPair;
  /** What the closing count was compared with (open shift: the live countBasis). */
  readonly expected: CurrencyPair;
  /** Closing count (null while open). */
  readonly counted: CurrencyPair | null;
  /** Σ closing differences (re-opened counts included), signed; "0" = matched. */
  readonly difference: CurrencyPair;
  /** Worst status of the closing counts per currency: pending > rejected > approved > matched. */
  readonly differenceStatus: { readonly IQD: CashCountStatus | null; readonly USD: CashCountStatus | null };
  readonly openingDifference: CurrencyPair;
  readonly profits: CurrencyPair;
  readonly note: string | null;
}

export interface ShiftsView {
  readonly officeToday: string;
  readonly officeMonth: string;
  readonly branchId: string;
  readonly subSafesActive: boolean;
  readonly me: { readonly userId: string; readonly canOpenForOther: boolean; readonly canApprove: boolean };
  /** Users a shift can be opened for (the caller only, without `open_shift_for_other`). */
  readonly users: ReadonlyArray<{ readonly id: string; readonly username: string; readonly hasOpenShift: boolean }>;
  /** Live book balances of the caller's own drawers (the open-shift dialog shows them, D-120). */
  readonly myDrawers: { readonly IQD: DrawerFiguresView | null; readonly USD: DrawerFiguresView | null };
  readonly active: readonly ShiftLiveView[];
  readonly log: readonly ShiftRowView[];
  /** Old "المجموع الكلي التراكمي لترصيد الورديات (أدمن) - الشهر الحالي" over the office month (D-122). */
  readonly monthTotals: { readonly shifts: number; readonly opening: CurrencyPair; readonly closing: CurrencyPair; readonly profits: CurrencyPair; readonly net: CurrencyPair };
  /** Differences waiting for approval (approvers see all; others their own). */
  readonly pendingCounts: readonly CashCountView[];
}

/** `GET /shifts/drawers?ownerUserId=` — the book balances the open dialog shows for the chosen employee. */
export interface ShiftDrawersView {
  readonly ownerUserId: string;
  readonly drawers: { readonly IQD: DrawerFiguresView; readonly USD: DrawerFiguresView };
  readonly otherOpening: CurrencyPair;
  readonly debts: CurrencyPair;
  readonly trusts: CurrencyPair;
  readonly pendingProfits: CurrencyPair;
}

export interface ReportBoxLine {
  readonly accountId: string;
  readonly name: string;
  readonly kind: string;
  readonly currency: string;
  readonly opening: string;
  readonly closing: string;
  readonly variance: string;
}

export interface ReportOperationLine {
  readonly operationId: string;
  readonly seq: number;
  readonly type: string;
  readonly at: string;
  readonly operator: string | null;
  /** Drawer lines of the operation, signed per currency (+ in, − out). */
  readonly drawer: CurrencyPair;
  readonly correction: boolean;
}

/** `GET /shifts/:id/report` — the 2-page shift report and the opening print (D-122). */
export interface ShiftReportView {
  readonly shift: ShiftRowView;
  readonly openedBy: string;
  readonly closedBy: string | null;
  readonly drawers: { readonly IQD: DrawerFiguresView; readonly USD: DrawerFiguresView };
  readonly boxes: readonly ReportBoxLine[];
  readonly debts: { readonly opening: CurrencyPair; readonly closing: CurrencyPair };
  readonly trusts: { readonly opening: CurrencyPair; readonly closing: CurrencyPair };
  readonly profits: CurrencyPair;
  readonly counts: readonly CashCountView[];
  readonly appendix: readonly ReportOperationLine[];
  /** Print number issued once per shift report (D-147: later prints are copies). */
  readonly printedBefore: boolean;
}

export interface DayRowView {
  readonly id: string;
  readonly branchId: string;
  readonly officeDate: string;
  readonly open: boolean;
  /** D-302: a day not closed on its own date stays "not closed". */
  readonly expired: boolean;
  readonly openedBy: string;
  readonly openedAt: string;
  readonly closedBy: string | null;
  readonly closedAt: string | null;
  readonly opening: CurrencyPair;
  readonly openingCounted: CurrencyPair;
  readonly expected: CurrencyPair;
  readonly counted: CurrencyPair | null;
  readonly difference: CurrencyPair;
  readonly differenceStatus: { readonly IQD: CashCountStatus | null; readonly USD: CashCountStatus | null };
  readonly openingDifference: CurrencyPair;
  readonly note: string | null;
}

export interface DayLiveView {
  readonly day: DayRowView;
  /** The vault and all its custodies together; moves between them net out (D-298 (c)). */
  readonly totals: { readonly IQD: DrawerFiguresView; readonly USD: DrawerFiguresView };
  readonly expensesCount: number;
  readonly counts: readonly CashCountView[];
}

export interface DaysView {
  readonly officeToday: string;
  readonly branchId: string;
  /** `canSettle`: open / close the day (D-302 — `open_shift_for_other` or the Super Manager). Users who may not see every
   * shift (D-303) get no day figures: `today` null, empty log, zero book, their own pending counts. */
  readonly me: { readonly userId: string; readonly canApprove: boolean; readonly canPrint: boolean; readonly canSettle: boolean };
  /** Today's record (open or closed); null when the day was not opened yet. */
  readonly today: DayLiveView | null;
  /** The book of the vault set now (the open dialog shows it). */
  readonly book: { readonly IQD: DrawerFiguresView; readonly USD: DrawerFiguresView };
  readonly log: readonly DayRowView[];
  readonly pendingCounts: readonly CashCountView[];
}

/** `GET /days/:id/report` — the 3-page daily report (D-122). */
export interface DayReportView {
  readonly day: DayRowView;
  readonly totals: { readonly IQD: DrawerFiguresView; readonly USD: DrawerFiguresView };
  /** Per drawer of the set: the vault and each custody (D-121). */
  readonly drawers: readonly DrawerFiguresView[];
  readonly boxes: readonly ReportBoxLine[];
  readonly debts: { readonly opening: CurrencyPair; readonly closing: CurrencyPair };
  readonly trusts: { readonly opening: CurrencyPair; readonly closing: CurrencyPair };
  readonly profits: CurrencyPair;
  readonly counts: readonly CashCountView[];
  readonly appendix: readonly ReportOperationLine[];
  readonly printedBefore: boolean;
}

/** Results of the writes. */
export interface ShiftWriteResult {
  readonly shiftId: string;
  readonly counts: readonly CashCountView[];
}
export interface DayWriteResult {
  readonly dayId: string;
  readonly counts: readonly CashCountView[];
}
export interface CashCountDecisionResult {
  readonly count: CashCountView;
  /** D-301: counts re-opened by a rejection. */
  readonly reopened: readonly CashCountView[];
}
